import { CussStatusCode } from '@app/enums/cuss-device-status.enum';
import { CallbackContext } from '@app/cuss/embross/utils/callbacks/ssbd.callback';

/**
 * Superficie mínima que el callback necesita del servicio de papel de la
 * impresora de pases de abordar. Mantenerla aquí evita que esta utilidad
 * conozca servicios o componentes Angular.
 */
export interface BoardingPassPaperHandler {
  notifyMediaLow(): void;
  notifyMediaEmpty(): void;
  notifyMediaRecovered(): void;
}

/**
 * Convierte un valor crudo del SDK en un código de estado, aceptando ÚNICAMENTE
 * evidencia numérica explícita.
 *
 * Esta función existe para cerrar un fail-open: `Number()` coerciona `null`,
 * `''`, `false` y `[]` a `0`, que es precisamente `OK`. Con una coerción directa,
 * una consulta fallida, un payload incompleto o un fallback ausente se leería
 * como "papel repuesto" y retiraría un bloqueo de papel vacío real.
 *
 * Son INERTES (devuelven `null`): `null`, `undefined`, `NaN`, `''`, cadenas en
 * blanco, booleanos, arreglos, objetos y cualquier cadena no numérica. Ninguno
 * puede, por tanto, limpiar una condición `low` / `empty` ya conocida.
 *
 * @returns el código numérico, o `null` si el valor no es interpretable
 */
export function toBoardingPassStatusCode(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const code = Number(value);
    return Number.isFinite(code) ? code : null;
  }
  return null;
}

/**
 * Extrae el código numérico de una respuesta `statusIsOK`, cuyo formato crudo del
 * SDK es `"<status>,<isOK>"` (p. ej. `"107,1"`).
 *
 * El primer campo se valida con `toBoardingPassStatusCode`, de modo que una
 * respuesta truncada como `",1"` —cuyo primer campo es la cadena vacía— es inerte
 * en lugar de interpretarse como `0 OK`.
 *
 * @returns el código, o `null` si el valor no es interpretable
 */
export function parseBoardingPassStatusIsOK(value: unknown): number | null {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }
  return toBoardingPassStatusCode(value.split(',')[0]);
}

/**
 * Aplica un código de estado del ATB Printer (Boarding Pass) sobre el handler de papel.
 *
 * Solo se interpretan los códigos dentro del alcance de papel de CI-23161:
 * - `107 MEDIA_LOW`   → condición de papel bajo.
 * - `108 MEDIA_EMPTY` → condición de papel vacío.
 * - `0 OK`            → recuperación a estado normal.
 *
 * Cualquier otro código (101, 104, 301-310, …) se ignora deliberadamente para
 * conservar intacto el comportamiento previo del dispositivo.
 */
export function applyBoardingPassStatusCode(
  code: number | null,
  handler: BoardingPassPaperHandler | null | undefined
): void {
  if (code === null || !handler) {
    return;
  }
  if (code === CussStatusCode.MEDIA_LOW) {
    handler.notifyMediaLow();
    return;
  }
  if (code === CussStatusCode.MEDIA_EMPTY) {
    handler.notifyMediaEmpty();
    return;
  }
  if (code === CussStatusCode.OK) {
    handler.notifyMediaRecovered();
  }
}

/**
 * Crea el callback puro de papel del ATB_PRINTER — CI-23161.
 *
 * Sigue el mismo patrón que `createBagtagCallback`: una closure conserva el
 * contexto (no depende de `this`), interpreta `event.key` / `event.value` y
 * delega en un servicio Angular resuelto de forma lazy a través del
 * `CallbackContext`, evitando así una dependencia circular con `CussService`.
 *
 * Alcance estricto: únicamente `107 MEDIA_LOW`, `108 MEDIA_EMPTY` y `0 OK`. No se
 * maneja aquí ningún otro código —incluidos 301-309— para no alterar el
 * comportamiento existente de impresión ni duplicar su lógica.
 *
 * El segundo elemento de `event.value` es el flag `fatal` del SDK y no participa
 * en la decisión: solo se usa `event.value[0]`.
 */
export function createBoardingPassCallback(ctx: CallbackContext) {
  return (event: unknown): void => {
    try {
      const handler = ctx.getBoardingPassPaperService?.();
      if (!handler) {
        return;
      }

      const { key, value } = (event ?? {}) as { key?: unknown; value?: unknown };

      switch (key) {
        case 'statusChange': {
          const raw = Array.isArray(value) ? value[0] : value;
          applyBoardingPassStatusCode(toBoardingPassStatusCode(raw), handler);
          break;
        }

        case 'statusIsOK':
          applyBoardingPassStatusCode(parseBoardingPassStatusIsOK(value), handler);
          break;

        default:
          break;
      }
    } catch (err) {
      // Nunca propagar al SDK: una excepción aquí aborta el resto del despacho.
      console.error('[ATB] boarding pass paper callback error:', err);
    }
  };
}
